import {
  getDataFromLFStorage,
  LFStorageKeys,
} from "../../storage/LFStorage";
import Capabilities from "../capability";
import { isEmpty } from "lodash";
import store from "../../../state/configureStore";
import { BotLogsService } from "./LogsServices";
import tell from "../conversation/ReceiveMessage";
import { getAuthData } from "../../storage/LocalStorage";
import { UserServiceClientApi } from "../../api/UserApiServices";
import { parseBytesContent } from "../../../utils/general";
import { setUserMessagePoolTimestamp } from "../../../state/actions/chats";
import { NEW_SERVER_MESSAGE_RECEIVED } from "../../../state/constants";
import { handleTabStack } from "../../../state/actions/tabStack";
import { IntToMessageTypeConstants } from "../Message";


export let botContext = {};
let logsQueue = [];
let logActivityQueue = [];
let isLogEventAdded = false;
let isLogActivityEventAdded = false;


const processQueue = (queue, eventName) => {
  if (store.getState().user.isOnline) {
    while (queue.length) {
      const batch = queue.splice(0, 10);
      eventName === "log" ? BotLogsService.addLog(batch) : BotLogsService.addBatchLogEntry(batch);

      if (!queue.length) {
        if (eventName === "log") isLogEventAdded = false;
        else isLogActivityEventAdded = false;
      }
    }
  }
};


const addToQueue = (queue, data, eventName) => {
  queue.push(data);
  if (eventName === "log" && !isLogEventAdded) {
    isLogEventAdded = true;
    window.addEventListener("online", () => {
      setTimeout(() => processQueue(queue, "log"), 0);
    });
  } else if (eventName === "logActivity" && !isLogActivityEventAdded) {
    isLogActivityEventAdded = true;
    window.addEventListener("online", () => {
      setTimeout(() => processQueue(queue, "logActivity"), 0);
    });
  }
};


export const initializeBotContext = async (userDomain, botId) => {
  try {
    let userDomainBots = await getDataFromLFStorage(userDomain);
    let dependencyList = await getDataFromLFStorage(LFStorageKeys.DEPENDENCIES);
    console.log(`%c Initializing bot ${botId} for ${userDomain} domain`, "color: green;");

    if (userDomainBots?.length && dependencyList?.length) {
      botContext = userDomainBots.find((bot) => bot.botId === botId);
      botContext.botManifest = botContext;
      botContext.userDetails = getAuthData().user;
      botContext.botState = {};
      botContext.capabilities = { ...Capabilities };

      const dependency = botContext.dependencies;

      for (const key in dependency) {
        if (!isEmpty(dependency[key])) {
          const botDependency = dependencyList.find(
            (item) =>
              item.remoteDependencies === key &&
              item.version === dependency[key].version
          );

          if (botDependency && dependency[key].version === botDependency.version) {
            try {
              let botModule = eval(botDependency.Content);
              if (typeof botModule !== "object") {
                botModule = window.botModule; // Handle global botModule
              }
              botContext.capabilities[key] = botModule;
            } catch (error) {
              console.error(`Failed to eval the bot code for ${key}:`, error);
            }
          }
        }
      }

      botContext.getCapability = (name) => {
        if (botContext.capabilities[name]) {
          return botContext.capabilities[name];
        } else {
          console.error("Capability not supported: [" + name + "]");
        }
      };

      botContext.setConversationContext = (context) => {
        botContext.conversationContext = context;
      };

      botContext.getConversationContext = () => {
        return botContext.conversationContext;
      };

      botContext.updateConversationContextId = (contextId) => {
        botContext.updateConversationContextId(contextId);
      };

      botContext.getBotKey = () => {
        return botContext.botId;
      };

      botContext.getBotId = () => {
        return botContext.botId;
      };

      botContext.tell = (payload) =>
        new Promise((resolve) => {
          tell(payload, botContext);
          if (typeof payload === "string") {
            const Message = botContext.getCapability("Message");
            const message = new Message();
            message.stringMessage(payload);
            message.messageByBot(true);
            resolve(message);
          } else {
            payload.messageByBot(true);
            resolve(payload);
          }
        });

      botContext.done = () =>
        new Promise((resolve) => {
          resolve(botContext.done());
        });

      botContext.wait = (shouldWait = true) => shouldWait;

      botContext.log = (payload) => {
        // Checking for pending logs in queue first.
        if (logsQueue.length > 0) processQueue(logsQueue, "log");

        let data = processLogPayload(payload, getAuthData().user);
        if (store.getState().user.isOnline) {
          return new Promise((resolve, reject) => {
            if (botContext.log) {
              setTimeout(() => {
                BotLogsService.addLog([data])
                  .then(() => {
                    resolve();
                  })
                  .catch((err) => {
                    reject(err);
                    console.error("Error in bot context log: ", err);
                  });
              }, 2000);
            } else {
              resolve();
            }
          });
        } else {
          // Stacking the logs when edge device goes offline and processed by the eventListener
          logsQueue.push(data);
          if (!isLogEventAdded) {
            isLogEventAdded = true;
            window.addEventListener("online", () => {
              // The below call will be async with time 0ms as there is some delay in redux to set the state online
              setTimeout(() => {
                processQueue(logsQueue, "log")
              }, 0);
            });
          }
        }
      };

      botContext.logActivity = (payload) => {
        const data = payload;
        if (store.getState().user.isOnline) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              BotLogsService.addBatchLogEntry([data])
                .then(() => resolve())
                .catch((err) => {
                  reject(err);
                  console.error("Error in bot context logActivity:", err);
                });
            }, 2000);
          });
        } else {
          addToQueue(logActivityQueue, data, "logActivity");
          return Promise.resolve();
        }
      };

      botContext.devMode = (payload) =>
        new Promise((resolve) => {
          resolve(payload);
        });

      if (botContext?.botContent?.length) {
        try {
          let botModule = null;
          let myBot2 = eval(botContext.botContent);
          if (typeof myBot2 !== "object") {
            // The new framework doesn't return the bot object, it creates a global variable called botModule and assigns the bot to it
            myBot2 = botModule;
          }
          botContext.botContent = myBot2;
        } catch (error) {
          console.error("Failed to eval the bot content:", error);
        }
      }

      console.log("%c Initialized BotContext >> ", "color: green;", botContext);
      return botContext;
    }
  } catch (error) {
    console.error("ERROR within initializeBotContext : ", error);
  }
};

const processLogPayload = (params, user) => {
  const entry = params.entry;
  const { selectedDomain = null } = store.getState().domain.selectedDomain || {};
  const { selectedConversation = null } = store.getState().chats || {};

  if (user) {
    entry.selectedDomain = entry?.selectedDomain || selectedDomain;
    entry.botId = entry?.botId || selectedConversation?.bot?.botId;
    entry.botName = entry?.botName || selectedConversation?.bot?.botName;
    entry.userEmail = entry?.userEmail || user?.info?.emailAddress;
    entry.userId = entry?.userId || user?.userId;
    entry.timestamp = entry?.timestamp || Date.now();
    entry.entity = entry?.entity || 'webapp';

    if (typeof params.data !== 'string') {
      params.data = JSON.stringify(params.data);
    }
  }

  return {
    type: params.type,
    entry
  };
};


export const sendSocketReconnectionEvent = () => {
  if (isEmpty(botContext) || botContext.botId === 'im-bot') return;
  const Message = botContext.getCapability("Message");
  let message = new Message({ messageType: 'onSocketReconnection', messageTypeInt: 170 });
  botContext.botContent.open instanceof Function &&
    botContext.botContent.open(message, {}, [], botContext);
};

export const getUserDetails = async (userId) => {
  return UserServiceClientApi.fetchUserDetails({ userId })
    .then((userDetails) => {
      return userDetails;
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const callBotContextAsyncResult = async (rawMessage) => {
  console.log("%c Async message send from backend to bot(asyncResult) >>> ", "color: green;", rawMessage);
  // Add contentTypes from src\services\bot\capability\Message.js
  if (rawMessage.bot === "im-bot" && ["10", "30", "40", "60", "290", "310", "320", "330", "340",].includes(rawMessage.contentType.toString())) {
    // show the notification count even if some other bot is interacted with
    rawMessage.details = parseBytesContent(rawMessage.details);

    const message = {
      ...rawMessage,
      addedByBot: true,
      botKey: null,
      contentType: rawMessage.contentType.toString(),
      message: rawMessage.details[0].message,
      uuid: rawMessage.messageId,
      createdOn: new Date(rawMessage.createdOn),
      messageDate: new Date(rawMessage.createdOn),
      messageType: IntToMessageTypeConstants[rawMessage.contentType],
      options: null,
      status: 0,
    };
    delete message.details;

    // setting users pool timestamp if a new chat message comes, for notification count.
    setUserMessagePoolTimestamp(rawMessage?.createdOn)


    store.dispatch({
      type: NEW_SERVER_MESSAGE_RECEIVED,
      data: { message, conversationId: message.conversation },
    });

    //dispatching the action that handles notification count.
    store.dispatch(handleTabStack(message.conversation, message));
  }
};